import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DashboardLayout from '../../../Layout/DashboardLayout';
import moment from 'moment';
import { getFilteredDataBySystemId, downloadFile, downloadAllFiles } from '../../../services/invTreamentUsageDoc.handlers';

const SystemUsageDetailPage = () => {
  const { systemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Handle back button click to navigate to previous page
  const handleBack = () => {
    navigate(-1);
  };
  
  // Store the current systemId in a ref to access it in callbacks without violating hook rules
  const systemIdRef = React.useRef(systemId);
  
  // Keep systemIdRef up to date
  React.useEffect(() => {
    systemIdRef.current = systemId;
  }, [systemId]);
  
  // Use useRef to always have access to the latest URL parameters
  // This prevents stale closure issues with quickly changing params
  const queryParamsRef = React.useRef(new URLSearchParams(location.search));
  
  // Function to manually refresh URL parameters - used after operations that might change URL
  const refreshUrlParams = () => {
    const currentParams = new URLSearchParams(window.location.search);
    queryParamsRef.current = currentParams;
    return currentParams; // Return for immediate use
  };
  
  // Update ref whenever location changes
  React.useEffect(() => {
    queryParamsRef.current = new URLSearchParams(location.search);
  }, [location.search]);
  
  // Update ref specifically when systemId changes
  React.useEffect(() => {
    refreshUrlParams();
  }, [systemId]);
  
  // For initial render, also create a regular variable
  const queryParams = new URLSearchParams(location.search);
  
  const [loading, setLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null); // Store the ID of the file being downloaded
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Get date range from URL query parameters or set to undefined
  // If both parameters are missing, we won't use date filtering
  const hasDateParams = queryParams.has('startDate') && queryParams.has('endDate');
  const [startDate, setStartDate] = useState(
    hasDateParams ? queryParams.get('startDate') : undefined
  );
  const [endDate, setEndDate] = useState(
    hasDateParams ? queryParams.get('endDate') : undefined
  );
  // Track if we're using date filtering
  const [usingDateFilter, setUsingDateFilter] = useState(hasDateParams);

  // Helper function to determine what to display for treatment counter
  const formatTreatmentCounter = (value) => {
    // Check all possible zero-like conditions
    if (value === 0 || value === '0' || value === null || value === undefined || value === '') {
      return 'Partial Treatment';
    }
    return value;
  };

  const fetchData = async (sysId, start, end, pageNum, pageSize) => {
    // Don't set loading if we don't have a valid systemId
    if (!sysId) {
      return Promise.reject(new Error('No systemId provided'));
    }
  
    setLoading(true);
    
    try {
      // Use the systemIdRef to access the current systemId
      const currentSystemId = systemIdRef.current;
      
      // If systemId has changed since request was initiated, abort
      if (sysId !== currentSystemId) {
        setLoading(false);
        return Promise.reject(new Error('SystemId changed during request'));
      }
      
      const response = await getFilteredDataBySystemId(sysId, start, end, pageNum, pageSize);
      
      // Check again if systemId has changed after the response returns
      const latestSystemId = systemIdRef.current;
      if (sysId !== latestSystemId) {
        setLoading(false);
        return Promise.reject(new Error('SystemId changed after response'));
      }
      
      if (response && response.data) {
        setData(response.data.data || []);
        setTotalRecords(response.data.totalRecords || 0);
      } else {
        // Handle invalid response silently
        setData([]);
        setTotalRecords(0);
      }
      
      return response;
    } catch (error) {
      // Silently handle fetch errors
      setData([]);
      setTotalRecords(0);
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data when component mounts or dependencies change
    const pageNumber = page + 1; // API uses 1-based indexing
    
    // Don't make requests if systemId is missing or changing
    if (!systemId) {
      return;
    }
    
    // IMPORTANT: Use explicit null instead of undefined for non-date params
    // API requests need explicit null to ensure parameters aren't included
    const dateParams = (usingDateFilter && startDate && endDate) 
      ? { start: startDate, end: endDate } 
      : { start: null, end: null };
    
    // Prevent duplicate API calls by setting a flag
    let isMounted = true;
    
    // Make the API call with appropriate parameters
    fetchData(systemId, dateParams.start, dateParams.end, pageNumber, rowsPerPage)
      .then(() => {
        // Only update state if component is still mounted and systemId hasn't changed
        if (!isMounted || systemId !== systemIdRef.current) {
          
        }
      });
    
    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isMounted = false;
    };
  }, [systemId, startDate, endDate, usingDateFilter, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      setDownloadingFile(fileId); // Set the downloading file ID instead of the global loading state
      
      const response = await downloadFile(fileId);
      
      if (response.status && response.data) {
        // Create a blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `file_${fileId}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download file: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error downloading file: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloadingFile(null); // Clear the downloading file ID
      
      // Don't update date filter state after download - this was causing the unwanted behavior
      // The page state should remain as it was before the download
    }
  };

  const handleDownloadAllFiles = async () => {
    try {
      // Don't refresh URL parameters before download - this preserves the current state
      // Only read the current state to provide to the API
      
      // Only use date parameters if we're using filtering
      const effectiveStartDate = usingDateFilter ? startDate : undefined;
      const effectiveEndDate = usingDateFilter ? endDate : undefined;
      
      setDownloadingAll(true); // Use separate state for download all button
      
      // Only pass date parameters if we're using date filtering
      const response = usingDateFilter && startDate && endDate
        ? await downloadAllFiles(systemId, effectiveStartDate, effectiveEndDate)
        : await downloadAllFiles(systemId);
      
      if (response.status && response.data) {
        // Create a blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Use the filename provided by the backend
        a.download = `SystemUsageAllFiles_${systemId}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download files: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error downloading files: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloadingAll(false); // Clear the downloading all state
      
      // Don't update date filter state after download - this was causing the unwanted behavior
      // The page state should remain as it was before the download
    }
  };

  // Toggle date filter on/off
  const toggleDateFilter = (enable) => {
    // Reset to page 1 when changing filters
    const pageNumber = 1;
    setPage(0); // UI uses 0-based pagination
    
    // Get the EXACT current state at this moment
    // This is crucial for handling rapid changes correctly
    const currentSystemId = systemId; // Use the actual prop value, not the ref
    
    // Force a direct read of the URL parameters - don't rely on any stored state
    const currentParams = new URLSearchParams(window.location.search);
    const originalStartDate = currentParams.get('startDate');
    const originalEndDate = currentParams.get('endDate');
    
    // Update the ref to ensure future operations have the current values
    queryParamsRef.current = currentParams;
    
    // Cancel any pending operations
    setLoading(false);
    
    if (enable) {
      // RESTORE filter - enable date filtering
      if (originalStartDate && originalEndDate && currentSystemId) {
        // Update all state in a batch to avoid inconsistent state
        setStartDate(originalStartDate);
        setEndDate(originalEndDate);
        setUsingDateFilter(true);
        setLoading(true);
        
        // Use Promise to handle the API call
        getFilteredDataBySystemId(currentSystemId, originalStartDate, originalEndDate, pageNumber, rowsPerPage)
          .then(response => {
            if (response && response.data) {
              // Check if the current systemId is still the same
              // to avoid race conditions with quick changes
              if (currentSystemId === systemIdRef.current) {
                setData(response.data.data || []);
                setTotalRecords(response.data.totalRecords || 0);
              }
            }
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    } else {
      // REMOVE filter - disable date filtering
      
      // Update all relevant state in one batch
      setStartDate(undefined);
      setEndDate(undefined);
      setUsingDateFilter(false);
      setLoading(true);
      
      // Direct API call without date parameters
      getFilteredDataBySystemId(currentSystemId, null, null, pageNumber, rowsPerPage)
        .then(response => {
          if (response && response.data) {
            // Check systemId hasn't changed during the request
            if (currentSystemId === systemIdRef.current) {
              setData(response.data.data || []);
              setTotalRecords(response.data.totalRecords || 0);
            }
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const handleRestoreDateFilter = () => {
    toggleDateFilter(true);
  };
  
  const handleRemoveDateFilter = () => {
    toggleDateFilter(false);
  };

  // Use a more targeted approach instead of an effect with no dependencies
  // This could cause excessive rerenders and race conditions
  const getLatestUrlParams = () => {
    // Always get the most up-to-date URL params directly from window.location
    return new URLSearchParams(window.location.search);
  };
  
  return (
    <DashboardLayout title="Detail Page">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Treatment Record - {systemId}{usingDateFilter ? ` - ${moment(startDate).format('MMMM YYYY')}` : ' - All Data'}
          </Typography>
          {(() => {
            // Get current URL parameters DIRECTLY from window.location at render time
            // This ensures we have the absolute latest URL state without any caching or timing issues
            const currentParams = getLatestUrlParams();
            const hasCurrentDateParams = currentParams.has('startDate') && currentParams.has('endDate');
            
            
            // Show the appropriate button based on current filter state
            if (usingDateFilter) {
              return (
                <Button 
                  variant="contained" 
                  color="info" 
                  onClick={handleRemoveDateFilter}
                  sx={{ 
                    ml: 2,
                    minWidth: '180px',
                    height: '40px',
                    whiteSpace: 'nowrap',
                    bgcolor: '#1976d2', // Explicit blue color
                    '&:hover': {
                      bgcolor: '#1565c0' // Darker blue on hover
                    }
                  }}
                >
                  REMOVE DATE FILTER
                </Button>
              );
            } else if (hasCurrentDateParams) {
              return (
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleRestoreDateFilter}
                  sx={{ 
                    ml: 2,
                    minWidth: '180px',
                    height: '40px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  RESTORE DATE FILTER
                </Button>
              );
            }
            
            return null;
          })()}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={downloadingAll ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadAllFiles}
            disabled={downloadingAll}
            sx={{
              minWidth: '200px', 
              width: '200px',
              height: '40px',
              whiteSpace: 'nowrap'
            }}
          >
            {downloadingAll ? 'DOWNLOADING...' : 'DOWNLOAD ALL FILES'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ tableLayout: 'fixed' }}>
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>System Ser. No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell align="center">Treatment Counter</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length > 0 ? (
                    data.map((row) => (
                      <TableRow key={row.id} sx={{ height: '72px' }}>
                        <TableCell>{row.system_Id}</TableCell>
                        <TableCell>{moment(row.created_Date).format('YYYY/MM/DD')}</TableCell>
                        <TableCell>{moment(row.created_Date).format('HH:mm')} GMT</TableCell>
                        <TableCell align="center">
                          {formatTreatmentCounter(row.file_Treatment_NS)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <Button 
                              variant="contained" 
                              color="primary"
                              startIcon={downloadingFile === row.id ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                              onClick={() => handleDownloadFile(row.id, row.new_File_Name)}
                              disabled={downloadingFile === row.id}
                              sx={{ 
                                minWidth: '160px',
                                width: '160px',
                                height: '36px',
                                whiteSpace: 'nowrap',
                                '& .MuiButton-startIcon': {
                                  marginRight: '4px' 
                                }
                              }}
                            >
                              {downloadingFile === row.id ? 'DOWNLOADING...' : 'DOWNLOAD FILE'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              mt: 2,
              p: 1,
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>Rows per page:</Typography>
                <FormControl size="small">
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    displayEmpty
                    inputProps={{ 'aria-label': 'rows per page' }}
                    sx={{ 
                      minWidth: '70px',
                      width: '70px'
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          width: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Typography variant="body2" sx={{ mr: 2 }}>
                {data.length > 0 ? `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRecords)} of ${totalRecords}` : '0-0 of 0'}
              </Typography>
              
              <Box>
                <IconButton 
                  onClick={() => handleChangePage(null, page - 1)} 
                  disabled={page === 0}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <IconButton 
                  onClick={() => handleChangePage(null, page + 1)} 
                  disabled={page >= Math.ceil(totalRecords / rowsPerPage) - 1}
                >
                  <KeyboardArrowRightIcon />
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default SystemUsageDetailPage;
